import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { APIProvider, Map as GMap, AdvancedMarker, useMap } from "@vis.gl/react-google-maps";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  MapPin,
  Search,
  List,
  Map,
  Star,
  Phone,
  Navigation,
  Menu,
  X,
  Dumbbell,
  Users,
  Building2,
  ChevronRight,
  Filter,
} from "lucide-react";
import { API } from "@/App";
import { useLocalizedData } from "@/contexts/LocalizedDataContext";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const MAP_ID = 'e2229d09b8bbc0139f850641';

// Map markers sub-component (must be inside APIProvider)
function MapMarkers({ businesses, selectedBusiness, onBusinessClick, setInfoWindowOpen, setUserLocation }) {
  const map = useMap();
  
  useEffect(() => {
    if (map && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userPos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setUserLocation(userPos);
          map.panTo(userPos);
          map.setZoom(13);
        },
        () => {
          console.log("Geolocation permission denied");
        }
      );
    }
  }, [map, setUserLocation]);

  return (
    <>
      {businesses.map((business) => {
        const isAdminBusiness = business.name?.toLowerCase().includes('probody') || 
                                business.owner_email === 'ender@probodysystems.com';
        const isSelected = selectedBusiness?.business_id === business.business_id;
        return (
          <AdvancedMarker
            key={business.business_id}
            position={{
              lat: business.latitude || 41.0082,
              lng: business.longitude || 28.9784,
            }}
            onClick={() => {
              onBusinessClick(business);
              setInfoWindowOpen(business.business_id);
            }}
          >
            {isAdminBusiness ? (
              <div style={{
                width: 40, height: 40,
                background: isSelected ? '#DA291C' : '#F59E0B',
                borderRadius: '50%',
                border: '3px solid white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transform: isSelected ? 'scale(1.2)' : 'scale(1)',
                transition: 'transform 0.2s',
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
              </div>
            ) : (
              <div style={{
                width: 32, height: 32,
                background: isSelected ? '#DA291C' : '#1B365D',
                borderRadius: '50%',
                border: '3px solid white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                cursor: 'pointer',
                transform: isSelected ? 'scale(1.2)' : 'scale(1)',
                transition: 'transform 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                  <path d="M20.57 14.86L22 13.43 20.57 12 17 15.57 8.43 7 12 3.43 10.57 2 9.14 3.43 7.71 2 5.57 4.14 4.14 2.71 2.71 4.14l1.43 1.43L2 7.71l1.43 1.43L2 10.57 3.43 12 7 8.43 15.57 17 12 20.57 13.43 22l1.43-1.43L16.29 22l2.14-2.14 1.43 1.43 1.43-1.43-1.43-1.43L22 16.29z"/>
                </svg>
              </div>
            )}
          </AdvancedMarker>
        );
      })}
    </>
  );
}

// Business Card Component - Yatay Layout (Logo solda, detaylar sağda)
const BusinessCard = ({ business, onClick, selected }) => {
  const { t } = useTranslation(['landing', 'common']);
  const { getLocalizedBranch, getLocalizedTrainingType } = useLocalizedData();
  // Logo veya ilk galeri görseli
  const cardLogo = business.logo || business.images?.[0] || "https://images.pexels.com/photos/5412246/pexels-photo-5412246.jpeg";
  
  return (
    <Card
      data-testid={`business-card-${business.business_id}`}
      className={`business-card cursor-pointer border-2 transition-all hover:shadow-lg ${
        selected ? "border-navy shadow-md" : "border-transparent hover:border-navy/20"
      }`}
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex gap-4">
          {/* Sol: Logo */}
          <div className="flex-shrink-0 w-20 h-20 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 border border-slate-200 flex items-center justify-center overflow-hidden">
            <img
              src={cardLogo}
              alt={business.name}
              className="w-full h-full object-contain p-2"
            />
          </div>
          
          {/* Sağ: Detaylar */}
          <div className="flex-1 min-w-0">
            {/* İşletme Adı ve Tipi */}
            <div className="flex items-start justify-between gap-2 mb-1">
              <h3 className="font-heading font-bold text-navy text-base truncate">
                {business.name}
              </h3>
              <Badge variant="outline" className="text-xs flex-shrink-0">
                {business.business_type === "corporate" ? t('landing:corporate') : t('landing:individual')}
              </Badge>
            </div>
            
            {/* Konum */}
            <div className="flex items-center gap-1.5 text-grey text-sm mb-2">
              <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate">{business.city}, {business.district}</span>
            </div>
            
            {/* Rating */}
            <div className="flex items-center gap-1.5 mb-2">
              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
              <span className="font-semibold text-navy text-sm">{business.rating?.toFixed(1) || "0.0"}</span>
              <span className="text-grey text-xs">({business.rating_count || 0} {t('landing:reviews')})</span>
            </div>
            
            {/* Branşlar */}
            <div className="flex flex-wrap gap-1">
              {business.branches?.slice(0, 3).map((branch) => (
                <span key={branch} className="badge-branch text-xs px-2 py-0.5">
                  {getLocalizedBranch(branch)}
                </span>
              ))}
              {business.branches?.length > 3 && (
                <span className="badge-branch text-xs px-2 py-0.5">+{business.branches.length - 3}</span>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Header Component
const Header = ({ isScrolled, onMenuClick, user, businesses: userBusinesses, onLogout, guestMode, viewMode }) => {
  const navigate = useNavigate();
  const { t } = useTranslation(['landing', 'common']);
  
  // Get user's business name if logged in and has a business
  const userBusiness = userBusinesses?.[0];
  
  // Determine user role
  const isBusinessOwner = user?.role === "business" || userBusiness;
  const isTrainer = user?.role === "trainer";
  const isEmployee = user?.role === "employee";
  const isMember = user?.role === "member";
  
  // Liste görünümünde header her zaman navy olsun
  const showNavyHeader = viewMode === "list" || !isScrolled;

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (error) {
      console.error("Logout error:", error);
    }
    
    // Clear all storage
    localStorage.removeItem('auth_token');
    sessionStorage.setItem('just_logged_out', 'true');
    
    // Clear local state
    if (onLogout) onLogout();
    
    // Force full page reload
    window.location.href = "/";
  };

  return (
    <header
      className="fixed top-0 left-0 right-0 z-[1001] transition-all duration-300 backdrop-blur-sm shadow-md"
      style={{ backgroundColor: '#1B365D' }}
    >
      <div className="container mx-auto px-4 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img 
            src="https://customer-assets.emergentagent.com/job_273f8795-754a-4865-8c8d-c4895c64dfec/artifacts/y1zkpgvm_Probody-Studio%20v2_01.png" 
            alt="Probody Studio Logo" 
            className="w-10 h-10 object-contain"
          />
          <span className="font-heading font-bold text-xl text-white">
            PBStudio
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-6">
          {/* Antrenörler - visible to everyone */}
          <Link
            to="/trainers"
            className="font-medium transition-colors flex items-center gap-2 text-white/80 hover:text-white"
          >
            <Dumbbell className="w-4 h-4" />
            {t('landing:nav.trainers')}
          </Link>
          
          {user ? (
            // Logged in - show navigation based on role
            <>
              {/* İşletme Paneli - for business owners, trainers and employees */}
              {(isBusinessOwner || isTrainer || isEmployee) && (
                <Link
                  to="/dashboard"
                  className="font-medium transition-colors flex items-center gap-2 text-white/80 hover:text-white"
                >
                  <Building2 className="w-4 h-4" />
                  {t('landing:nav.businessDashboard')}
                </Link>
              )}
              
              {/* Üye Paneli - for everyone */}
              <Link
                to="/member"
                className="font-medium transition-colors flex items-center gap-2 text-white/80 hover:text-white"
              >
                <Users className="w-4 h-4" />
                {t('landing:nav.memberDashboard')}
              </Link>
              
              {/* User/Business name and logout */}
              <div className="flex items-center gap-3">
                <LanguageSwitcher variant="minimal" className="text-white/80 hover:text-white" />
                <span className="text-sm font-medium text-white">
                  {isBusinessOwner && userBusiness ? userBusiness.name : user.name}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleLogout}
                  className="rounded-xl text-red-300 hover:bg-white/10"
                >
                  {t('common:logout')}
                </Button>
              </div>
            </>
          ) : (
            // Not logged in - show login button
            <>
              <LanguageSwitcher variant="minimal" className="text-white/80 hover:text-white" />
              <Link
                to="/auth"
                className="font-medium transition-colors text-white/80 hover:text-white"
              >
                {t('landing:nav.login')}
              </Link>
              <Button
                data-testid="register-business-btn"
                onClick={() => navigate("/auth?type=business&mode=register")}
                className="bg-red-500 hover:bg-red-600 text-white rounded-xl"
              >
                {t('landing:nav.register')}
              </Button>
            </>
          )}
        </nav>

        <button
          className="md:hidden p-2"
          onClick={onMenuClick}
          data-testid="mobile-menu-btn"
        >
          <Menu className="text-white" />
        </button>
      </div>
    </header>
  );
};

export default function LandingPage({ guestMode = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation(['landing', 'common']);
  const { getLocalizedBranch, getLocalizedTrainingType } = useLocalizedData();
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("map"); // map or list
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedBranch, setSelectedBranch] = useState("");
  const [selectedCard, setSelectedCard] = useState("");
  const [cities, setCities] = useState([]);
  const [branches, setBranches] = useState([]);
  const [memberCards, setMemberCards] = useState([]);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [infoWindowOpen, setInfoWindowOpen] = useState(null);
  
  // User state
  const [currentUser, setCurrentUser] = useState(null);
  const [userBusinesses, setUserBusinesses] = useState([]);

  // Default center (Istanbul)
  const defaultCenter = { lat: 41.0082, lng: 28.9784 };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    fetchData();
  }, []);
  
  // Handle logout state from navigation
  useEffect(() => {
    if (location.state?.loggedOut) {
      setCurrentUser(null);
      setUserBusinesses([]);
      // Clear the state to prevent issues on refresh
      navigate("/", { replace: true, state: {} });
    }
  }, [location.state]);
  
  // Check user session on every page visit/focus
  useEffect(() => {
    // Don't check if just logged out or if logout flag is set
    const wasJustLoggedOut = sessionStorage.getItem('just_logged_out');
    if (wasJustLoggedOut) {
      sessionStorage.removeItem('just_logged_out');
      setCurrentUser(null);
      setUserBusinesses([]);
      return;
    }
    
    if (!location.state?.loggedOut) {
      checkUserSession();
    }
    
    // Also check when window gets focus (user might have logged out in another tab)
    const handleFocus = () => {
      const loggedOut = sessionStorage.getItem('just_logged_out');
      if (!loggedOut) {
        checkUserSession();
      }
    };
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, []);
  
  const checkUserSession = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await fetch(`${API}/auth/me`, {
        credentials: "include",
        headers,
      });

      if (response.ok) {
        const userData = await response.json();
        setCurrentUser(userData);

        // İşletme paneline girebilecek tüm yetkililer (Sahip, Antrenör, Çalışan)
        const isBizUser = 
          userData.role === "business" || 
          userData.role === "trainer" || 
          userData.role === "employee" ||
          userData.business_id;

        if (userData.role === "business") {
          const bizRes = await fetch(`${API}/my-businesses`, {
            credentials: "include",
            headers,
          });
          if (bizRes.ok) {
            const bizData = await bizRes.json();
            setUserBusinesses(bizData);
          }
        }

        // Eğer kullanıcı menüden bilerek Harita'ya basmadıysa (yani doğrudan siteye girdiyse) panele gönder
        // Sol menüden haritaya basıldığında veya URL'de '?map=1' varsa haritada kalır
        const urlParams = new URLSearchParams(window.location.search);
        const userWantsMap = urlParams.get("map") === "1" || guestMode;

        if (!userWantsMap) {
          if (isBizUser) {
            navigate("/dashboard", { replace: true });
          } else {
            navigate("/member", { replace: true });
          }
        }
      } else {
        setCurrentUser(null);
        setUserBusinesses([]);
      }
    } catch (error) {
      setCurrentUser(null);
      setUserBusinesses([]);
    }
  };
  
  // Handle logout
  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (error) {
      console.error("Logout error:", error);
    }
    
    // Clear all storage
    localStorage.removeItem('auth_token');
    sessionStorage.setItem('just_logged_out', 'true');
    
    // Clear local state
    setCurrentUser(null);
    setUserBusinesses([]);
    
    // Force full page reload to clear all cached state and cookies
    window.location.href = "/";
  };
  
  const fetchData = async () => {
    try {
      const [businessesRes, citiesRes, branchesRes, cardsRes] = await Promise.all([
        fetch(`${API}/businesses`),
        fetch(`${API}/cities`),
        fetch(`${API}/public/branches`),
        fetch(`${API}/public/member-cards`),
      ]);

      if (businessesRes.ok) {
        const data = await businessesRes.json();
        setBusinesses(data);
        console.log("Businesses loaded:", data.length);
      }
      if (citiesRes.ok) {
        const citiesData = await citiesRes.json();
        setCities(citiesData);
        console.log("Cities loaded:", citiesData);
      }
      if (branchesRes.ok) {
        const branchData = await branchesRes.json();
        // Handle both array of objects and array of strings
        const normalizedBranches = (Array.isArray(branchData) ? branchData : []).map(b => {
          if (typeof b === 'string') return { id: b, name: b };
          return b;
        });
        setBranches(normalizedBranches);
        console.log("Branches loaded:", normalizedBranches);
      }
      if (cardsRes.ok) {
        const cardsData = await cardsRes.json();
        const cards = cardsData.member_cards || [];
        setMemberCards(cards);
        console.log("Member cards loaded:", cards);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredBusinesses = businesses.filter((b) => {
    if (searchQuery && !b.name.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    if (selectedCity && selectedCity !== "all" && b.city !== selectedCity) {
      return false;
    }
    if (selectedBranch && selectedBranch !== "all" && !b.branches?.includes(selectedBranch)) {
      return false;
    }
    // Filter by accepted card (e.g., MultiSport, Mediatopia)
    if (selectedCard && selectedCard !== "all" && !b.accepted_cards?.includes(selectedCard)) {
      return false;
    }
    return true;
  });

  const handleBusinessClick = (business) => {
    setSelectedBusiness(business);
    if (viewMode === "list") {
      navigate(`/business/${business.business_id}`);
    }
  };

  const handleNavigate = (business) => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${business.latitude},${business.longitude}`;
    window.open(url, "_blank");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header 
        isScrolled={isScrolled} 
        viewMode={viewMode}
        onMenuClick={() => setShowMobileMenu(true)}
        user={currentUser}
        businesses={userBusinesses}
        guestMode={guestMode}
        onLogout={() => {
          setCurrentUser(null);
          setUserBusinesses([]);
        }}
      />

      {/* Hero Section with Map */}
      <div className="relative">
        {viewMode === "map" ? (
          <div className="h-screen">
            {!GOOGLE_MAPS_API_KEY ? (
              <div className="h-full flex flex-col items-center justify-center bg-slate-100 p-4">
                <p className="text-red-500 font-medium mb-2">{t('landing:mapLoadFailed')}</p>
                <p className="text-sm text-gray-600 text-center max-w-md">
                  Google Maps API anahtarı bulunamadı. Lütfen .env dosyasını kontrol edin.
                </p>
              </div>
            ) : (
              <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                <GMap
                  mapId={MAP_ID}
                  defaultCenter={defaultCenter}
                  defaultZoom={12}
                  disableDefaultUI={true}
                  zoomControl={true}
                  streetViewControl={false}
                  mapTypeControl={false}
                  fullscreenControl={false}
                  style={{ width: '100%', height: '100%' }}
                >
                  <MapMarkers
                    businesses={filteredBusinesses}
                    selectedBusiness={selectedBusiness}
                    onBusinessClick={handleBusinessClick}
                    setInfoWindowOpen={setInfoWindowOpen}
                    setUserLocation={setUserLocation}
                  />
                </GMap>
              </APIProvider>
            )}

            {/* Floating Search Bar */}
            <div className="absolute top-24 left-4 right-4 md:left-1/2 md:-translate-x-1/2 md:w-[600px] z-[1000]">
              <div className="glass-card rounded-2xl shadow-floating p-4">
                <div className="flex gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-grey" />
                    <Input
                      data-testid="search-input"
                      placeholder={t('landing:searchPlaceholder')}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 h-12 rounded-xl border-grey-200 focus:border-navy"
                    />
                  </div>
                  <Button
                    data-testid="filter-btn"
                    variant="outline"
                    className="h-12 px-4 rounded-xl border-grey-200"
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    <Filter className="w-5 h-5" />
                  </Button>
                </div>

                {showFilters && (
                  <div className="flex flex-wrap gap-3 mt-3 animate-fade-in relative z-[1000]">
                    <Select value={selectedCity} onValueChange={setSelectedCity}>
                      <SelectTrigger data-testid="city-filter" className="h-10 rounded-xl w-[140px] bg-white">
                        <SelectValue placeholder={t('landing:filterByCity')} />
                      </SelectTrigger>
                      <SelectContent className="z-[1001]">
                        <SelectItem value="all">{t('landing:allCities')}</SelectItem>
                        {cities.map((city) => (
                          <SelectItem key={city} value={city}>
                            {city}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                      <SelectTrigger data-testid="branch-filter" className="h-10 rounded-xl w-[140px] bg-white">
                        <SelectValue placeholder={t('common:branch')} />
                      </SelectTrigger>
                      <SelectContent className="z-[1001]">
                        <SelectItem value="all">{t('landing:allBranches')}</SelectItem>
                        {branches.map((branch) => (
                          <SelectItem key={branch.id} value={branch.name}>
                            {getLocalizedBranch(branch.name)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    <Select value={selectedCard} onValueChange={setSelectedCard}>
                      <SelectTrigger data-testid="card-filter" className="h-10 rounded-xl w-[140px] bg-white">
                        <SelectValue placeholder={t('landing:filterByCard')} />
                      </SelectTrigger>
                      <SelectContent className="z-[1001]">
                        <SelectItem value="all">{t('landing:allCards')}</SelectItem>
                        {memberCards.map((card) => (
                          <SelectItem key={card} value={card}>
                            {card}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>

            {/* View Toggle */}
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-[1000]">
              <div className="glass-card rounded-full shadow-floating p-1 flex">
                <Button
                  data-testid="map-view-btn"
                  variant={viewMode === "map" ? "default" : "ghost"}
                  className={`rounded-full px-6 ${viewMode === "map" ? "bg-navy" : ""}`}
                  onClick={() => setViewMode("map")}
                >
                  <Map className="w-4 h-4 mr-2" />
                  {t('landing:mapView')}
                </Button>
                <Button
                  data-testid="list-view-btn"
                  variant={viewMode === "list" ? "default" : "ghost"}
                  className={`rounded-full px-6 ${viewMode === "list" ? "bg-navy" : ""}`}
                  onClick={() => setViewMode("list")}
                >
                  <List className="w-4 h-4 mr-2" />
                  {t('landing:listView')}
                </Button>
              </div>
            </div>

            {/* Business Count Badge */}
            <div className="absolute bottom-8 right-4 z-[1000]">
              <div className="glass-card rounded-xl shadow-md px-4 py-2 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-navy" />
                <span className="font-semibold text-navy">{filteredBusinesses.length}</span>
                <span className="text-grey">{t('landing:businessCount', { count: filteredBusinesses.length }).split(' ').slice(1).join(' ')}</span>
              </div>
            </div>

            {/* Selected Business Card */}
            {selectedBusiness && (
              <div className="absolute bottom-24 left-4 right-4 md:left-4 md:right-auto md:w-80 z-[1000] animate-slide-up">
                <Card className="glass-card shadow-floating">
                  <CardContent className="p-4">
                    <div className="flex gap-3">
                      {/* Show image with proper sizing */}
                      <div className="w-14 h-14 rounded-xl bg-white border flex items-center justify-center flex-shrink-0 overflow-hidden">
                        <img
                          src={selectedBusiness.logo || selectedBusiness.images?.[0] || "https://images.pexels.com/photos/5412246/pexels-photo-5412246.jpeg"}
                          alt=""
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-heading font-bold text-navy truncate">{selectedBusiness.name}</h3>
                        <div className="flex items-center gap-1 text-sm text-grey mt-1">
                          <MapPin className="w-3 h-3" />
                          <span className="truncate">{selectedBusiness.city}</span>
                        </div>
                        <div className="flex items-center gap-1 mt-1">
                          <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                          <span className="font-semibold text-sm">{selectedBusiness.rating?.toFixed(1) || "0.0"}</span>
                        </div>
                      </div>
                      <button
                        className="self-start p-1 hover:bg-grey-100 rounded-lg transition-colors flex-shrink-0"
                        onClick={() => setSelectedBusiness(null)}
                      >
                        <X className="w-5 h-5 text-grey" />
                      </button>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <Button
                        className="flex-1 bg-navy hover:bg-navy/90 rounded-xl"
                        onClick={() => navigate(`/business/${selectedBusiness.business_id}`)}
                        data-testid="selected-view-details"
                      >
                        Detayları Gör
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                      <Button
                        variant="outline"
                        className="rounded-xl"
                        onClick={() => handleNavigate(selectedBusiness)}
                      >
                        <Navigation className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        ) : (
          /* List View */
          <div className="pt-24 pb-8 px-4 md:px-8 min-h-screen">
            <div className="max-w-7xl mx-auto">
              {/* Search Bar for List View */}
              <div className="mb-8">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-grey" />
                    <Input
                      data-testid="list-search-input"
                      placeholder={t('landing:searchPlaceholder')}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 h-12 rounded-xl"
                    />
                  </div>
                  <Select value={selectedCity} onValueChange={setSelectedCity}>
                    <SelectTrigger className="w-full md:w-48 h-12 rounded-xl">
                      <SelectValue placeholder={t('landing:filterByCity')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('landing:allCities')}</SelectItem>
                      {cities.map((city) => (
                        <SelectItem key={city} value={city}>
                          {city}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                    <SelectTrigger className="w-full md:w-48 h-12 rounded-xl">
                      <SelectValue placeholder={t('common:branch')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('landing:allBranches')}</SelectItem>
                      {branches.map((branch) => (
                        <SelectItem key={branch.id} value={branch.name}>
                          {getLocalizedBranch(branch.name)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={selectedCard} onValueChange={setSelectedCard}>
                    <SelectTrigger className="w-full md:w-48 h-12 rounded-xl">
                      <SelectValue placeholder={t('landing:filterByCard')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('landing:allCards')}</SelectItem>
                      {memberCards.map((card) => (
                        <SelectItem key={card} value={card}>
                          {card}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Results Count */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading font-bold text-2xl text-navy">
                  {filteredBusinesses.length} {t('landing:businessCount', { count: filteredBusinesses.length }).split(' ').slice(1).join(' ')}
                </h2>
                <div className="flex gap-2">
                  <Button
                    variant={viewMode === "map" ? "default" : "outline"}
                    size="sm"
                    className="rounded-xl"
                    onClick={() => setViewMode("map")}
                  >
                    <Map className="w-4 h-4 mr-1" />
                    {t('landing:mapView')}
                  </Button>
                  <Button
                    variant={viewMode === "list" ? "default" : "outline"}
                    size="sm"
                    className="rounded-xl bg-navy"
                    onClick={() => setViewMode("list")}
                  >
                    <List className="w-4 h-4 mr-1" />
                    {t('landing:listView')}
                  </Button>
                </div>
              </div>

              {/* Business Grid */}
              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div key={i} className="skeleton h-72 rounded-xl" />
                  ))}
                </div>
              ) : filteredBusinesses.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredBusinesses.map((business) => (
                    <BusinessCard
                      key={business.business_id}
                      business={business}
                      onClick={() => handleBusinessClick(business)}
                      selected={selectedBusiness?.business_id === business.business_id}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <Dumbbell className="w-16 h-16 text-grey mx-auto mb-4" />
                  <h3 className="font-heading font-bold text-xl text-navy mb-2">
                    {t('landing:noBusinesses')}
                  </h3>
                  <p className="text-grey">
                    {t('common:noResults')}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Mobile Menu */}
      {showMobileMenu && (
        <div className="fixed inset-0 z-[2000] md:hidden">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowMobileMenu(false)}
          />
          <div className="absolute right-0 top-0 bottom-0 w-72 bg-white shadow-xl animate-slide-in">
            <div className="p-4 border-b flex items-center justify-between">
              <span className="font-heading font-bold text-navy">{t('landing:nav.home')}</span>
              <div className="flex items-center gap-2">
                <LanguageSwitcher variant="minimal" />
                <button onClick={() => setShowMobileMenu(false)}>
                  <X className="w-6 h-6 text-grey" />
                </button>
              </div>
            </div>
            <nav className="p-4 space-y-4">
              <Link
                to="/"
                className="block py-2 font-medium text-grey-700 hover:text-navy"
                onClick={() => setShowMobileMenu(false)}
              >
                {t('landing:mapView')}
              </Link>
              
              <Link
                to="/trainers"
                className="block py-2 font-medium text-grey-700 hover:text-navy flex items-center gap-2"
                onClick={() => setShowMobileMenu(false)}
              >
                <Dumbbell className="w-4 h-4" />
                {t('landing:nav.trainers')}
              </Link>
              
              {currentUser ? (
                // Logged in user menu
                <>
                  {/* İşletme Paneli - for business owners, trainers and employees */}
                  {(currentUser.role === "business" || currentUser.role === "trainer" || currentUser.role === "employee" || userBusinesses?.length > 0) && (
                    <Link
                      to="/dashboard"
                      className="block py-2 font-medium text-grey-700 hover:text-navy flex items-center gap-2"
                      onClick={() => setShowMobileMenu(false)}
                    >
                      <Building2 className="w-4 h-4" />
                      {t('landing:nav.businessDashboard')}
                    </Link>
                  )}
                  
                  <Link
                    to="/member"
                    className="block py-2 font-medium text-grey-700 hover:text-navy flex items-center gap-2"
                    onClick={() => setShowMobileMenu(false)}
                  >
                    <Users className="w-4 h-4" />
                    {t('landing:nav.memberDashboard')}
                  </Link>
                  
                  <div className="pt-4 border-t">
                    <p className="text-sm text-grey mb-2">
                      {userBusinesses?.[0]?.name || currentUser.name}
                    </p>
                    <Button
                      variant="outline"
                      className="w-full text-red-500 border-red-200 hover:bg-red-50"
                      onClick={() => {
                        setShowMobileMenu(false);
                        handleLogout();
                      }}
                    >
                      Çıkış Yap
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <Link
                    to="/auth"
                    className="block py-2 font-medium text-grey-700 hover:text-navy"
                    onClick={() => setShowMobileMenu(false)}
                  >
                    {t('landing:nav.login')}
                  </Link>
                  <Button
                    className="w-full bg-red-500 hover:bg-red-600 rounded-xl"
                    onClick={() => {
                      setShowMobileMenu(false);
                      navigate("/auth?type=business&mode=register");
                    }}
                  >
                    {t('landing:nav.register')}
                  </Button>
                </>
              )}
            </nav>
          </div>
        </div>
      )}
    </div>
  );
}
